import React, { useEffect } from 'react';
import {
  Container,
  HorizontalSeparator,
  ItemContainer,
  SimpleGraphContainer
} from './styles';
import { ICreditLimits } from '../../../../store/ducks/credit-limits/types';
import {
  formatCurrency,
  formatPercentualNumbers
} from '../../../../utils/formatNumbers';
import { SimpleGraph } from '@gerdau/hefesto-mais-web/SimpleGraph';
import { KeyValueItem } from '@gerdau/hefesto-mais-web';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../store/ducks/tagging/actions';

interface CreditDetailsModalContentProps {
  creditLimit: ICreditLimits;
}

const CreditDetailsModalContent: React.FC<CreditDetailsModalContentProps> = ({
  creditLimit
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'LG:Financas:MinhasFinancas:ModalDetalhesCredito',
        event: 'page_view'
      })
    );
  }, [dispatch]);

  return (
    <>
      <Container data-testid="creditDetailsModal">
        <ItemContainer>
          <div className="item-column">
            <h4 className="item-column__subtitle">{creditLimit.name}</h4>
            <span>Grupo empresarial</span>
          </div>

          <div className="item-column">
            <KeyValueItem title="Limite de crédito: ">
              {`R$ ${formatCurrency(creditLimit.creditLimit)}`}
            </KeyValueItem>
            <SimpleGraphContainer>
              <SimpleGraph
                percentage={Number(
                  formatPercentualNumbers(
                    creditLimit.total.usedCreditPercentage
                  )
                )}
              >
                {`${Number(
                  formatPercentualNumbers(
                    creditLimit.total.usedCreditPercentage
                  )
                )}%`}
              </SimpleGraph>
            </SimpleGraphContainer>
          </div>

          <div className="item-column">
            <KeyValueItem title="Pedidos em carteira: ">
              {formatPercentualNumbers(
                creditLimit.parentOpenPortifolioCreditPercentage
              )}
              % | R$ {formatCurrency(creditLimit.parentOpenPortifolioCredit)}
            </KeyValueItem>
          </div>

          <div className="item-column">
            <KeyValueItem title="NFs em aberto: ">
              {formatPercentualNumbers(
                creditLimit.total.openInvoiceUsedCreditPercentage
              )}
              % | R$ {formatCurrency(creditLimit.total.openInvoiceUsedCredit)}
            </KeyValueItem>
          </div>
        </ItemContainer>

        <HorizontalSeparator />

        {creditLimit.usedCreditPercentage && (
          <ItemContainer>
            <div className="item-column">
              <h4 className="item-column__subtitle">{creditLimit.name}</h4>
              <div
                className="item-column__content"
                title={`${creditLimit.locality.address} - ${creditLimit.locality.city} - CEP ${creditLimit.locality.zipcode}`}
              >
                {`${creditLimit.locality.address} - ${creditLimit.locality.city} - CEP ${creditLimit.locality.zipcode}`}
              </div>
            </div>

            <div className="item-column">
              <SimpleGraphContainer>
                <SimpleGraph
                  percentage={Number(
                    formatPercentualNumbers(creditLimit.usedCreditPercentage)
                  )}
                >
                  {`${Number(
                    formatPercentualNumbers(creditLimit.usedCreditPercentage)
                  )}%`}
                </SimpleGraph>
              </SimpleGraphContainer>
            </div>

            {creditLimit.openPortifolioUsedCreditPercentage &&
              Number(creditLimit.openPortifolioUsedCreditPercentage) > 0 && (
                <div className="item-column">
                  <KeyValueItem title="Pedidos em carteira: ">
                    {formatPercentualNumbers(
                      creditLimit.openPortifolioUsedCreditPercentage
                    )}
                    % | R${' '}
                    {formatCurrency(creditLimit.openPortifolioUsedCredit)}
                  </KeyValueItem>
                </div>
              )}

            {Number(creditLimit.total.openInvoiceUsedCreditPercentage) > 0 && (
              <div className="item-column">
                <KeyValueItem title="NFs em aberto: ">
                  {formatPercentualNumbers(
                    creditLimit.total.openInvoiceUsedCreditPercentage
                  )}
                  % | R${' '}
                  {formatCurrency(creditLimit.total.openInvoiceUsedCredit)}
                </KeyValueItem>
              </div>
            )}

            {Number(creditLimit.total.billedUsedCreditPercentage) > 0 && (
              <div className="item-column">
                <KeyValueItem title="Outros: ">
                  {formatPercentualNumbers(
                    creditLimit.total.billedUsedCreditPercentage
                  )}
                  % | R$ {formatCurrency(creditLimit.total.billedUsedCredit)}
                </KeyValueItem>
              </div>
            )}
          </ItemContainer>
        )}

        {creditLimit.subsidiaries.map((subsidiary, idx) => (
          <React.Fragment key={idx}>
            {(creditLimit.usedCreditPercentage || idx > 0) && (
              <HorizontalSeparator />
            )}

            <ItemContainer>
              <div className="item-column">
                <h4 className="item-column__subtitle">{subsidiary.name}</h4>
                <div
                  className="item-column__content"
                  title={`${subsidiary.locality.address} - ${subsidiary.locality.city} - CEP ${subsidiary.locality.zipcode}`}
                >
                  {subsidiary.locality.address} - {subsidiary.locality.city} -
                  CEP {subsidiary.locality.zipcode}
                </div>
              </div>

              <div className="item-column">
                <SimpleGraphContainer>
                  <SimpleGraph
                    percentage={Number(
                      formatPercentualNumbers(subsidiary.usedCreditPercentage)
                    )}
                  >
                    {`${Number(
                      formatPercentualNumbers(subsidiary.usedCreditPercentage)
                    )}%`}
                  </SimpleGraph>
                </SimpleGraphContainer>
              </div>

              <div className="item-column">
                <KeyValueItem title="Pedidos em carteira: ">
                  {formatPercentualNumbers(
                    subsidiary.openPortifolioUsedCreditPercentage
                  )}
                  % | R$ {formatCurrency(subsidiary.openPortifolioUsedCredit)}
                </KeyValueItem>
              </div>

              {Number(subsidiary.openInvoiceUsedCreditPercentage) > 0 && (
                <>
                  <div className="item-column">
                    <KeyValueItem title="NFs em aberto: ">
                      {formatPercentualNumbers(
                        subsidiary.openInvoiceUsedCreditPercentage
                      )}
                      % | R$ {formatCurrency(subsidiary.openInvoiceUsedCredit)}
                    </KeyValueItem>
                  </div>
                </>
              )}

              {Number(subsidiary.billedUsedCreditPercentage) > 0 && (
                <>
                  <div className="item-column">
                    <KeyValueItem title="NFs em aberto: ">
                      {formatPercentualNumbers(
                        subsidiary.billedUsedCreditPercentage
                      )}
                      % | R$ {formatCurrency(subsidiary.billedUsedCredit)}
                    </KeyValueItem>
                  </div>
                </>
              )}
            </ItemContainer>
          </React.Fragment>
        ))}
      </Container>
    </>
  );
};

export default CreditDetailsModalContent;
